import React from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  Stack,
  Typography,
} from "@mui/material";
import {
  DuplicateDecision,
  ExistingInquiryLite,
  SubmitInquiryInput,
} from "../hooks/useSubmitInquiry";


interface DuplicateDialogProps {
  open: boolean;
  onClose: () => void;
  existing: ExistingInquiryLite | null;
  input: SubmitInquiryInput | null;
  onDecision: (decision: DuplicateDecision) => void;
}

export function DuplicateDialog({
  open,
  onClose,
  existing,
  input,
  onDecision,
}: DuplicateDialogProps) {
  const differs =
    !!existing?.residentKey &&
    !!input?.residentKey &&
    existing.residentKey !== input.residentKey;

  // 🔁 Auto-resolve when it's clearly a different resident:
  // - immediately choose "different-resident"
  // - close the dialog
  // - ensure it only runs once per open cycle
  const autoHandledRef = React.useRef(false);
  React.useEffect(() => {
    if (!open) {
      autoHandledRef.current = false;
      return;
    }
    if (differs && !autoHandledRef.current) {
      autoHandledRef.current = true;
      onDecision("different-resident");
      onClose();
    }
  }, [open, differs, onDecision, onClose]);

  const handleDecision = (decision: DuplicateDecision) => {
    onDecision(decision);
    onClose();
  };

  return (
    <Dialog
      open={open}
      // ⛔️ do NOT force a "cancel" decision on close; just close
      onClose={onClose}
      fullWidth
      maxWidth="sm"
    >
      <DialogTitle>We found an existing request</DialogTitle>

      <DialogContent dividers>
        {existing ? (
          <Stack spacing={1}>
            <Typography variant="body2" color="text.secondary">
              You already have a pending <b>{existing.kind}</b> request.
            </Typography>
            <Typography variant="body2" color="text.secondary">
              Created:{" "}
              {existing.createdAt
                ? new Date(existing.createdAt).toLocaleString()
                : "—"}
              {existing.desiredDate
                ? ` • Preferred date: ${existing.desiredDate}`
                : ""}
            </Typography>

            <Typography variant="body2" color="text.secondary">
              Resident on file: <b>{existing.residentKey || "—"}</b>
              {input?.residentKey ? (
                <>
                  {" "}
                  • You entered: <b>{input.residentKey}</b>
                </>
              ) : null}
            </Typography>

            {/* If they differ we auto-resolve above; this message is for same-resident only */}
            {!differs ? (
              <Typography variant="body2" sx={{ mt: 1 }}>
                Is this for the same resident or a different one?
              </Typography>
            ) : null}
          </Stack>
        ) : (
          <Typography>Loading…</Typography>
        )}
      </DialogContent>

      {/* For same-resident case show choices; for different-resident we already closed */}
      {!differs && (
        <DialogActions>
          <Button
            onClick={() => handleDecision("use-existing")}
            variant="outlined"
          >
            Use Existing
          </Button>
          <Button onClick={() => handleDecision("fix-typo")} color="secondary">
            Fix Typo / Edit
          </Button>
          <Button
            onClick={() => handleDecision("different-resident")}
            variant="contained"
          >
            Different Resident
          </Button>
        </DialogActions>
      )}
    </Dialog>
  );
}
